import { Box, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { Feedback, FeedbackType, UserInfo } from "./types/queries";
import { ReactComponent as ShiftIcon } from "../../common/icons/clock.svg";
import { ReactComponent as UserIcon } from "../../common/icons/user.svg";
import { ReactComponent as WorkLocationIcon } from "../../common/icons/navigation/home.svg";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useAppSelector } from "../../app/hooks";
import { leadingZerosDateFormat } from "../../common/helpers/formatDateTime";
import { getShift } from "./helpers";
import {
  ErrorOutline as RelatesToIcon,
  ThumbDownAlt as ThumbDownIcon,
  ThumbUpAlt as ThumbUpIcon,
} from "@mui/icons-material/";

import { Sentiment } from "./types/commands";
import { getName } from "../../common/helpers/common";

type Props = {
  feedback: Feedback;
  onClick: (id: string) => void;
};

const FeedbackRow = ({ feedback, onClick }: Props) => {
  const { t } = useTranslation("feedbacks");

  const { workTitle } = useAppSelector((state) => state.localized);

  const renderTypography = (text?: string | number) => (
    <Typography fontSize="14px">{text}</Typography>
  );

  const renderFullName = (user: UserInfo) => renderTypography(getName(user));

  const renderFeedbackType = () => {
    const getIcon = () => {
      switch (feedback.type) {
        case FeedbackType.CUSTOMER_FEEDBACK_WORKTIMEENTRY:
        case FeedbackType.TALENT_FEEDBACK_WORKTIMEENTRY:
          return <ShiftIcon />;
        case FeedbackType.CUSTOMER_FEEDBACK_TALENT:
          return <UserIcon />;
        case FeedbackType.TALENT_FEEDBACK_WORKLOCATION:
          // TODO: this renders white, needs wrapper component used in messaging
          return <WorkLocationIcon />;
        default:
          return null;
      }
    };

    return (
      <Tooltip title={feedback?.relatesTo ? t(feedback?.relatesTo) : null}>
        <Box display="flex" alignItems="center" gap="10px" flexWrap="nowrap">
          <Box display="flex" alignItems="center" gap="4px">
            {feedback?.relatesTo && (
              <RelatesToIcon
                color="warning"
                sx={{ width: "22px", height: "22px" }}
              />
            )}
            {getIcon()}
          </Box>
          {renderTypography(t(feedback.type))}
        </Box>
      </Tooltip>
    );
  };

  const renderShift = () => {
    const shift = getShift(workTitle, feedback.worktimeEntry);
    return shift ? renderTypography(shift) : null;
  };

  const renderStatus = () => {
    const renderWithIndicator = (text: string, ack: boolean = true) => (
      <Box display="flex" alignItems="center" gap="10px" flexWrap="nowrap">
        <Box
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "100%",
            backgroundColor: ack ? "success.main" : "error.main",
          }}
        />
        {renderTypography(text)}
      </Box>
    );

    if (feedback?.ackByUserId === "system-user")
      return renderWithIndicator(t("autoAcknowledged"));
    if (feedback?.ackByUserId && feedback?.ackByUserId !== "system-user")
      return renderWithIndicator(t("acknowledged"));
    return renderWithIndicator(t("notAcknowledged"), false);
  };

  const renderTextFeedback = () => {
    return (
      <Box
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {renderTypography(feedback?.comment)}
      </Box>
    );
  };

  const sentimentIcon = !feedback.sentiment ? null : feedback.sentiment ===
    Sentiment.POSITIVE ? (
    <ThumbUpIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />
  ) : (
    <ThumbDownIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />
  );

  return (
    <TableRow onClick={() => onClick(feedback.id)}>
      <TableCell>{renderFullName(feedback.reporter)}</TableCell>
      <TableCell>{renderTypography(feedback?.workLocationName)}</TableCell>
      <TableCell>{renderFeedbackType()}</TableCell>
      <TableCell>{renderShift()}</TableCell>
      <TableCell>{renderFullName(feedback.talent)}</TableCell>
      <TableCell align="center">{sentimentIcon}</TableCell>
      <TableCell width="25%">{renderTextFeedback()}</TableCell>
      <TableCell>
        {renderTypography(
          format(new Date(feedback.createdAt), leadingZerosDateFormat),
        )}
      </TableCell>
      <TableCell>{renderStatus()}</TableCell>
    </TableRow>
  );
};

export default FeedbackRow;
